import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Text } from "../ui/text";
import CustomSelect from "../shared/custom-select";
import { BaseDialog } from "@/types/dialog";
import useChatFeedbackMutation, {
  type DissatisfactionType,
} from "@/hooks/services/mutations/ai-agent/use-chat-feedback-mutation";

const options = [
  { name: "Report a bug", value: "REPORT_A_BUG" },
  { name: "Untrue", value: "UNTRUE" },
  { name: "Incomplete response", value: "INCOMPLETE_RESPONSE" },
  { name: "Report content", value: "REPORT_CONTENT" },
  { name: "ETC", value: "ETC" },
];

interface AnswerFeedbackDialogProps extends BaseDialog {
  isLiked: boolean;
  chatId: number;
  chatHistoryId: number;
}

const AnswerFeedbackDialog = ({
  isOpen,
  onClose,
  isLiked = false,
  chatId,
  chatHistoryId,
}: AnswerFeedbackDialogProps) => {
  const [reason, setReason] = useState("");
  const [dissatisfactionType, setDissatisfactionType] =
    useState<DissatisfactionType>("REPORT_A_BUG");

  const { mutate: submitFeedback, isPending } = useChatFeedbackMutation();

  const handleSubmit = () => {
    submitFeedback(
      {
        chatId,
        chatHistoryId,
        body: {
          isPositive: isLiked,
          reason,
          ...(!isLiked && { dissatisfactionType }),
        },
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`w-[501px] ${isLiked ? "h-[439px]" : "h-[578px]"} rounded-[10px] pb-[32px]`}>
        <DialogHeader className="text-start px-10 pb-2 h-[60px] flex justify-end items-start">
          <DialogTitle>
            <Text variant="title-xxl">Feedback</Text>
          </DialogTitle>
        </DialogHeader>
        <div className="px-[24px]">
          {!isLiked && (
            <div className="flex flex-col px-4">
              <div className="flex py-[14px]">
                <Text variant="title-md" className="text-grey-50">
                  What type of problem is it? (optional)
                </Text>
              </div>
              <CustomSelect
                options={options}
                value={dissatisfactionType}
                onChange={(v) => setDissatisfactionType(v as DissatisfactionType)}
                className="border-grey-40 w-[200px] h-[48px] [&_span]:text-[14px] [&_span]:leading-[23px] [&_span]:tracking-[0.15px]"
                placeholder="Select type"
              />
            </div>
          )}
          <div className={`flex ${isLiked ? "pt-[22px] pb-[14px]" : "py-[14px]"} px-4`}>
            <Text variant="title-md" className="text-grey-50">
              Please write your opinion (optional)
            </Text>
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="px-4 py-[14px] bg-grey-95 rounded-[4px] w-full resize-none h-[164px]"
            placeholder={
              isLiked
                ? "What was satisfactory about this response?"
                : "What part of this response did you find unsatisfactory?"
            }
          />
          <div className="py-[14px] px-4">
            <Text variant="body-regular" className="text-grey-50">
              Submitting this report will send the entire current <br />{" "}
              conversation to CellKey AI for future model improvements.
            </Text>
          </div>
          <div className="flex justify-end gap-[10px] pt-[10px]">
            <Button variant="secondary" size={"md"} onClick={onClose}>
              <Text variant="title-md">Cancel</Text>
            </Button>
            <Button
              variant="primary"
              size={"md"}
              onClick={handleSubmit}
              disabled={isPending}
            >
              <Text variant="title-md" className="text-white">
                Send
              </Text>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnswerFeedbackDialog;
